import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { ActivityFeedStatusEnum } from 'redux-store/api/activity-feed';
import { createHashtagsArray, createMentionsArray } from 'utils/helpers';
import { DraftActionType, DraftType, GetPayloadState, UseDraftsArgs } from './types';

export const getMediaType = (fileName: string): 'image' | 'video' | 'audio' | 'file' => {
  if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|heic|heif)$/i.test(fileName)) return 'image';
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(fileName)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(fileName)) return 'audio';
  return 'file';
};

const isEmptyContent = (content: string): boolean => {
  return !content.replace(/<[^>]+>|&nbsp;/g, '').trim();
};

const processDraftContent = (
  editorState: UseDraftsArgs['editorState'],
  content: UseDraftsArgs['content']
) => {
  const rawDraftContent = convertToRaw(editorState.getCurrentContent());
  const draftHtml = draftToHtml(rawDraftContent);
  const finalContent = content || draftHtml;

  return {
    mentions: createMentionsArray(rawDraftContent),
    hashtagMentions: createHashtagsArray(rawDraftContent),
    sanitizedContent: isEmptyContent(finalContent) ? '' : finalContent,
  };
};

export const getPayload = (
  { editorState, data, content, form, id }: GetPayloadState,
  actionType: DraftActionType
) => {
  const { mentions, hashtagMentions, sanitizedContent } = processDraftContent(editorState, content);
  const { uploads, projectId, audience, phaseIds, phaseItemIds } = data;
  const { postId, rootCommentId, replyToCommentId } = form.getValues();

  const common = {
    projectId,
    content: sanitizedContent,
    mentions,
    hashtagMentions,
    status: ActivityFeedStatusEnum.DRAFT,
  };

  const commentUploads = uploads?.map((upload) => upload.id);

  const actionsMap = {
    CREATE_POST: () => ({
      ...common,
      ...audience,
      uploads,
      phaseIds,
      phaseItemIds,
    }),
    UPDATE_POST: () => ({
      ...common,
      ...audience,
      uploads,
      phaseIds,
      phaseItemIds,
      id,
    }),
    DELETE_POST: () => ({
      ids: [id],
      projectId,
      status: ActivityFeedStatusEnum.DRAFT,
    }),
    CREATE_COMMENT: () => ({
      ...common,
      uploads: commentUploads,
      postId,
      rootCommentId,
      replyToCommentId,
    }),
    UPDATE_COMMENT: () => ({
      ...common,
      uploads: commentUploads,
      postId,
      id,
    }),
    DELETE_COMMENT: () => ({
      ids: [id],
      postId,
      projectId,
      rootCommentId,
      status: ActivityFeedStatusEnum.DRAFT,
    }),
  };

  return actionsMap[actionType]();
};

export const isEmptyDraft = (
  content: UseDraftsArgs['content'],
  uploads: UseDraftsArgs['data']['uploads']
) => {
  return !content?.trim() && !uploads?.length;
};

export const getDraftType = (type: string): DraftType => {
  return type.includes('POST') ? 'POST' : 'COMMENT';
};
